import React from 'react';
import { CircularProgress, type CircularProgressProps } from '@mui/material';
import { colors } from '../../tokens/colors';

/**
 * Spinner compact pour boutons, champs, lignes de liste.
 * Préserve une échelle de tailles et la couleur marque par défaut.
 */
const PRESET_SIZES = {
  xs: 14,
  sm: 16,
  md: 18,
  lg: 20,
  xl: 22,
  '2xl': 24,
} as const;

export type InlineSpinnerPreset = keyof typeof PRESET_SIZES;
export type InlineSpinnerColor = 'brand' | 'inherit' | 'muted';

export type InlineSpinnerProps = Omit<CircularProgressProps, 'size' | 'color'> & {
  size?: InlineSpinnerPreset | number;
  color?: InlineSpinnerColor;
};

function resolvePx(size: InlineSpinnerProps['size']): number {
  if (size == null) return PRESET_SIZES.sm;
  if (typeof size === 'number') return size;
  return PRESET_SIZES[size];
}

function colorSx(color: InlineSpinnerColor): Record<string, string> {
  switch (color) {
    case 'inherit':
      return { color: 'inherit' };
    case 'muted':
      return { color: '#6b7280' };
    default:
      return { color: colors.primary[400] };
  }
}

export const InlineSpinner: React.FC<InlineSpinnerProps> = ({
  size = 'sm',
  color = 'brand',
  thickness,
  sx,
  ...rest
}) => {
  const px = resolvePx(size);
  const t = thickness ?? (px <= 16 ? 4 : 3);

  return (
    <CircularProgress
      size={px}
      thickness={t}
      sx={{
        ...colorSx(color),
        ...sx,
      }}
      {...rest}
    />
  );
};

export default InlineSpinner;
